import { useEffect, useMemo, useState } from "react";
import {
  Card,
  Layout,
  Form,
  Row,
  Col,
  Input,
  Button,
  Select,
  Upload,
  Image,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import requets from "../../api";
import { uploadEntityImage } from "../../api/service/cdn/cdn";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppAuth } from "../../context/use-app-auth";

const { Content } = Layout;

const normalizeUploadFile = (e) => {
  if (Array.isArray(e)) return e;
  return e?.fileList;
};

export default function ProductEdit() {
  const { t } = useTranslation("global");
  const auth = useAppAuth();
  const req = useMemo(() => requets(auth.user?.access_token), [auth.user?.access_token]);
  const { uuid } = useParams();
  const [form] = Form.useForm();
  const [messageSend, contextHolder] = message.useMessage();
  const [measurements, setMeasurements] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [currentIconUrl, setCurrentIconUrl] = useState(null);

  useEffect(() => {
    const run = async () => {
      try {
        const [productRes, measurementsRes] = await Promise.all([
          req.get(`/product/${uuid}`),
          req.get("/measurement", { limit: 500 }),
        ]);

        const p = productRes.data;
        setCurrentIconUrl(p.icon || null);
        form.setFieldsValue({
          name: p.name,
          description: p.description,
          type: p.type,
          version: p.version,
          measurements: p.productMeasurements?.map((pm) => pm.measurement?.uuid) ?? [],
          icon: p.icon
            ? [{ uid: "-1", name: "icon", status: "done", url: p.icon }]
            : [],
        });

        const raw = measurementsRes.data;
        const dataArray = Array.isArray(raw) ? raw : (raw?.data || []);
        setMeasurements(dataArray.map((m) => ({ label: m.name, value: m.uuid })));
      } catch {
        messageSend.error(t("products.errorLoad"));
      }
    };

    run();
  }, [uuid]);

  const onFinish = async (value) => {
    setUploading(true);
    try {
      let iconUrl = currentIconUrl;
      const iconFile = value.icon?.[0];
      if (iconFile?.originFileObj) {
        const updated = await uploadEntityImage(req, `/product/${uuid}/image`, iconFile.originFileObj);
        iconUrl = updated?.icon ?? currentIconUrl;
      }

      await req.put(`/product/${uuid}`, {
        name: value.name,
        description: value.description,
        type: value.type,
        version: value.version,
        measurements: value.measurements,
        icon: iconUrl,
      });
      messageSend.success(t("products.successUpdated"));
    } catch {
      messageSend.error(t("products.errorLoad"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Content>
      {contextHolder}
      <Card bordered={false}>
        <Form layout="vertical" form={form} onFinish={onFinish}>
          <Row wrap gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col xs={24} sm={12} md={8} lg={7}>
              <Form.Item
                label={t("products.tableColumn.name")}
                name="name"
                rules={[{ required: true, message: t("products.requiredName") }]}
              >
                <Input placeholder={t("products.tableColumn.placeholderName")} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={7}>
              <Form.Item
                label={t("products.tableColumn.productType")}
                name="type"
                rules={[{ required: true, message: t("products.requiredType") }]}
              >
                <Input placeholder={t("products.tableColumn.placeholderType")} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={7}>
              <Form.Item
                label={t("products.tableColumn.version")}
                name="version"
                rules={[{ required: true, message: t("products.requiredVersion") }]}
              >
                <Input placeholder={t("products.tableColumn.placeholderVersion")} />
              </Form.Item>
            </Col>
          </Row>
          <Row wrap gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item name="description" label={t("products.tableColumn.description")}>
                <Input placeholder={t("products.tableColumn.placeholderDescription")} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                label={t("products.tableColumn.newMeasurements")}
                name="measurements"
                rules={[{ required: true, message: t("products.requiredMeasurements") }]}
              >
                <Select
                  mode="tags"
                  size="large"
                  allowClear
                  placeholder={t("products.tableColumn.placeholderMeasurements")}
                  style={{ width: "100%" }}
                  options={measurements}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row wrap gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
            <Col xs={24} sm={8} md={6} lg={4}>
              <Form.Item
                label={t("products.tableColumn.icon")}
                name="icon"
                valuePropName="fileList"
                getValueFromEvent={normalizeUploadFile}
              >
                <Upload
                  listType="picture-card"
                  maxCount={1}
                  beforeUpload={() => false}
                  accept=".jpg,.jpeg,.png,.webp"
                >
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>{t("products.changeButton")}</div>
                  </div>
                </Upload>
              </Form.Item>
            </Col>
            {currentIconUrl && (
              <Col
                xs={24}
                sm={8}
                md={6}
                lg={4}
                style={{ display: "flex", alignItems: "center" }}
              >
                <div>
                  <div style={{ marginBottom: 4, fontSize: 12, color: "rgba(0,0,0,0.45)" }}>
                    {t("products.currentIcon")}
                  </div>
                  <Image
                    src={currentIconUrl}
                    alt={t("products.currentIcon")}
                    width={80}
                    style={{ objectFit: "cover", borderRadius: 8 }}
                  />
                </div>
              </Col>
            )}
          </Row>
          <Form.Item>
            <Button shape="round" htmlType="submit" type="primary" loading={uploading}>
              {t("deviceEdit.saveChange")}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </Content>
  );
}
